import './tooltip.css';

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import parse from 'html-react-parser';
import * as React from 'react';
import { PropsWithChildren } from 'react';

import { cn } from '@/services/utils';

import { Icon } from '../Icon';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipContent = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 10, ...props }, ref) => (
    <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
            `scoped-tooltip
            tw-z-[1060]
            tw-overflow-hidden
            tw-shadow-md
            tw-px-6
            tw-py-3
            tw-text-t-sm
            tw-animate-in
            tw-fade-in-0
            tw-zoom-in-95
            data-[state=closed]:tw-animate-out
            data-[state=closed]:tw-fade-out-0
            data-[state=closed]:tw-zoom-out-95
            data-[side=bottom]:tw-slide-in-from-top-2
            data-[side=left]:tw-slide-in-from-right-2
            data-[side=right]:tw-slide-in-from-left-2
            data-[side=top]:tw-slide-in-from-bottom-2`,
            className,
        )}
        {...props}
    />
));

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

type TooltipPropsType = {
    popupContent: string | null;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    /** Extra classes for the trigger wrapper. Use this when the trigger must shrink, such as a truncated label. */
    triggerClassName?: string;
};

const Tooltip = ({
    popupContent,
    children,
    size = 'sm',
    triggerClassName,
}: PropsWithChildren<TooltipPropsType>) => {
    const contents = (
        // Ensure the direct child is a span, otherwise it'll not work
        <span className={cn('tw-cursor-pointer tw-flex tw-items-center tw-justify-center', triggerClassName)}>
            {children ? (
                children
            ) : (
                <Icon icon={QuestionMarkCircleIcon} variant="info" size={size} />
            )}
        </span>
    );

    return popupContent ? (
        <TooltipPrimitive.Root delayDuration={300}>
            <TooltipPrimitive.TooltipTrigger asChild>
                {contents}
            </TooltipPrimitive.TooltipTrigger>
            <TooltipPrimitive.Portal >
                <TooltipContent className="tw-max-w-[450px] tw-text-left">
                    <TooltipPrimitive.Arrow className="scoped-tooltip-arrow" />
                    {parse(popupContent)}
                </TooltipContent>
            </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
    ) : (
        <>{contents}</>
    );
};

export { Tooltip, TooltipProvider };
